import {
  forwardRef,
  useRef,
  type AriaRole,
  type JSX,
  type ReactNode,
} from 'react';
import { useIntl } from 'react-intl';

import { PieChartLegend } from './PieChartLegend/PieChartLegend.js';
import { PieChartOverlays } from './PieChartOverlays/PieChartOverlays.js';
import { PieChartPlot } from './PieChartPlot/PieChartPlot.js';
import { useBehavioralTrackingProps } from '../../../core/hooks/useBehavioralTrackingProps.js';
import { useMergeRefs } from '../../../core/hooks/useMergeRefs.js';
import { ChartLayout } from '../../core/components/chart-layout/ChartLayout.js';
import { useAutoLegendRefresh } from '../../core/components/chart-layout/hooks/useAutoLegendRefresh.js';
import { OverrideRatioFunction } from '../../core/components/chart-layout/types/chart-layout.js';
import { OverlayProvider } from '../../core/components/overlay-tooltip/contexts/OverlayContext.js';
import { TooltipProvider } from '../../core/components/overlay-tooltip/contexts/TooltipContext.js';
import { ORIENTATION } from '../../core/constants.js';
import type { AriaLabel } from '../../core/types/aria-label.js';
import { sanitizeLegendRatio } from '../../core/utils/sanitize-legend-ratio.js';
import { useChartLegend } from '../context/ChartLegendContext.js';
import { usePieChartRawData } from '../context/PieChartRawDataContext.js';
import { PIE_CHART_MESSAGES } from '../messages.js';
import { PieChartSlicesDataProvider } from '../providers/PieChartSlicesDataProvider.js';
import {
  DEFAULT_CONTAINER_PADDING,
  DEFAULT_SIZE,
  PIE_CATEGORICAL_LEGEND_SIZES,
} from '../types/default.js';
import type { ChartVariant } from '../types/internal/pie-chart.js';
import { PieChartProps } from '../types/pie-chart.config.js';

type PieChartInternalProps = Omit<PieChartProps, 'data'> &
  AriaLabel & {
    role?: AriaRole;
    variant: ChartVariant;
    height?: string | number;
    width?: string | number;
    isEmpty?: boolean;
    emptyState?: ReactNode;
    errorState?: ReactNode | ((message: string) => JSX.Element);
    'data-dt-component': string;
  };

export const PieChartInternal = forwardRef<
  HTMLDivElement,
  PieChartInternalProps
>((props, forwardedRef) => {
  const {
    height,
    width,
    isEmpty,
    emptyState,
    errorState,
    loading = false,
    className,
    style,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    'data-testid': dataTestId,
    'aria-label': ariaLabel,
    'data-dt-component': dataDtComponent,
    role,
  } = props;

  const [behavioralTrackingProps] = useBehavioralTrackingProps(props);

  const {
    hidden: legendHidden,
    position: legendPosition,
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    ratio,
    initialRatio,
    onRatioChange,
  } = useChartLegend();

  const legendRatio = initialRatio ?? ratio;

  const plotAreaRef = useRef<HTMLDivElement>(null);
  const mergedRef = useMergeRefs([forwardedRef, plotAreaRef]);
  const intl = useIntl();

  const chartData = usePieChartRawData();
  const isInvalidDataset =
    chartData.slices?.reduce((acc, slice) => acc + slice.value, 0) <= 0;
  const invalidState =
    !isEmpty && isInvalidDataset
      ? emptyState ?? intl.formatMessage(PIE_CHART_MESSAGES.invalidDataset)
      : emptyState;

  const overrideRatio: OverrideRatioFunction = (
    orientation,
    width,
    height,
    ratio,
    legendContainerDimensions,
  ) => {
    const isHorizontal = orientation === ORIENTATION.Horizontal;

    if (!isHorizontal) {
      return legendContainerDimensions;
    }

    const sanitizedRatio = sanitizeLegendRatio(ratio);
    const legendHeight = isHorizontal ? height : sanitizedRatio * height * 0.01;
    let legendWidth = width;

    if (isHorizontal) {
      legendWidth =
        ratio === 'auto' ? width - height : width * sanitizedRatio * 0.01;
    }

    return {
      width: legendWidth,
      height: legendHeight,
    };
  };

  const chartLayoutRef = useAutoLegendRefresh(chartData);

  return (
    <OverlayProvider>
      <TooltipProvider>
        <ChartLayout
          {...behavioralTrackingProps}
          ref={chartLayoutRef}
          chartWidth={width ?? '100%'}
          chartHeight={height ?? DEFAULT_SIZE}
          isEmpty={isEmpty || isInvalidDataset}
          emptyState={isEmpty ? emptyState : emptyState ?? invalidState}
          errorState={errorState}
          showLoader={loading}
          className={className}
          style={{ padding: `0 ${DEFAULT_CONTAINER_PADDING}`, ...style }}
          data-dtrum-mask={dataDtrumMask}
          data-dtrum-allow={dataDtrumAllow}
          data-dt-component={dataDtComponent}
          data-testid={dataTestId}
          aria-label={ariaLabel}
          role={role}
        >
          <ChartLayout.Provider provider={PieChartSlicesDataProvider}>
            <ChartLayout.Graph>
              <PieChartOverlays>
                <PieChartPlot {...props} ref={mergedRef} />
              </PieChartOverlays>
            </ChartLayout.Graph>
            {!legendHidden && (
              <ChartLayout.Legend
                position={legendPosition}
                initialRatio={legendRatio}
                ratioBoundaries={PIE_CATEGORICAL_LEGEND_SIZES}
                overrideRatio={overrideRatio}
                onResize={onRatioChange}
              >
                <PieChartLegend />
              </ChartLayout.Legend>
            )}
          </ChartLayout.Provider>
        </ChartLayout>
      </TooltipProvider>
    </OverlayProvider>
  );
});

(
  PieChartInternal as typeof PieChartInternal & { displayName: string }
).displayName = 'PieChartInternal';
